import Location from "../../hooks/Location";
import {
  useFirestore,
  useFirestoreLoaded,
  useFirestoreOrderBy,
  useFirestoreGeneral,
  useFirestoreGeneralIn,
  useFirestoreGeneralOrderBy,
} from "../../firebase/useFirestore";
import { useEffect, useState, useContext, useMemo } from "react";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import { client } from "../../hooks/Client";
import { Link, useHistory, useLocation } from "react-router-dom";
import ScrollToTop from "../../hooks/ScrollToTop";
import { Auth } from "../../StateManagment/Auth";
import CopyLink from "../../components/common/CopyLink";
import Tooltip from "../../components/common/Tooltip";
import QrcodeIcon from "../../images/icons/qrcode-icon.png";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppLink from "../../components/common/WhatsAppLink";
import languages from "../../utils/listOfLanguages";
import KioskLink from "../../components/common/KioskLink";
import KioskLinkBoring from "../../components/common/KioskLinkBoring";
import PersonaMeta from "../../components/personas/PersonaMeta";
import { ADDRESS_LABEL_TITLES } from "../../helpers/adressHelpers/householdFields";
import buildRespondentGroups from "../../helpers/questionnaires/buildRespondentGroups";
import summarizeMomentQuestions from "../../helpers/questionnaire/summarizeMomentQuestions";
import notDeleted from "../../helpers/notDeleted";
import { buildStreetLabelMap } from "../../helpers/adressHelpers/buildStreetLabels";
import buildQuestionnaireShareLinks from "../../helpers/questionnaires/buildQuestionnaireShareLinks";
import {
  LINK_SETTINGS,
  buildLinkSettingsQuery,
} from "../../helpers/questionnaires/linkSettings";
import { buildShareQuestionnairePath, readShareQuestionnairePath } from "../../helpers/questionnaires/shareQuestionnairePath";
import isFlagOn from "../../helpers/isFlagOn";

const ShareQuestionnaire = () => {
  // Context
  const [auth] = useContext(Auth);

  // Hooks
  const history = useHistory();
  const { search } = useLocation();
  // Eén lezer voor deze url, naast de bouwer die alle afnemers gebruiken; terug
  // van de QR-pagina komen meetmoment en uitgezette instellingen mee.
  // AI-CONTEXT: See docs/pitfalls.md#routing-url-built-here-read-by-position-there
  const {
    questionnaireID: questionnaire,
    researchID: research,
    respondentID: personaFromUrl,
    measureMomentID: momentFromUrl,
    offKeys: offKeysFromUrl,
  } = readShareQuestionnairePath(Location(), search);
  ScrollToTop();

  // State
  const [measureMomentID, setMeasureMomentID] = useState(momentFromUrl);
  const [selectedLanguage, setSelectedLanguage] = useState("nl");
  // Instellingen van het onderzoek die deze link uitzet.
  const [disabledSettings, setDisabledSettings] = useState(/** @type {string[]} */ (offKeysFromUrl));

  // If there's a persona in the URL, use it; otherwise default to 'undefined' (anonymous)
  const [selectedPersona, setSelectedPersona] = useState(personaFromUrl || "undefined");

  // Firestore
  const researches = useFirestoreGeneral("Research", "ID", research ? research : "");
  const measureMomentsRaw = useFirestoreGeneralOrderBy(
    "MeasureMoments",
    "ResearchID",
    research ? research : "",
    "Position",
    "asc"
  );
  const measureMoments = useMemo(() => (measureMomentsRaw || []).filter((m) => !m.Deleted), [measureMomentsRaw]);
  const personas = useFirestore("Personas");
  const questionnaireFields = (useFirestoreGeneralOrderBy(
    "QuestionnaireFields",
    "QuestionnaireID",
    questionnaire ? questionnaire : "",
    "Position",
    "asc"
  ) || []).filter(notDeleted);
  const adresses = useFirestoreOrderBy("Adresses", "Position", "asc");

  // Zonder onderzoek in de URL (lege staat op het dashboard, Decca's knop, of de
  // URL zelf) valt er niets te delen. Dan moet je het onderzoek hier alsnog
  // kunnen kiezen. `loaded` scheidt 'nog aan het laden' van 'echt geen
  // onderzoeken' — anders staat er een onwaar "nog geen onderzoeken" zolang de
  // read loopt.
  // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
  const [allResearches, researchesLoaded] = useFirestoreLoaded("Research");
  const streetData = useFirestoreGeneralIn("PersonaData", "Title", ADDRESS_LABEL_TITLES);

  // Huishoudens krijgen hun label uit hun vaste adres (en tot de migratie de
  // oude regels), dezelfde lezing als het huishoudens-overzicht.
  // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
  // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T9)
  const streetByAddressId = useMemo(() => buildStreetLabelMap(streetData, adresses), [streetData, adresses]);

  // Deelnemers/medewerkers en huishoudens in één lijst, elk onder een eigen kopje.
  const respondentGroups = useMemo(
    () => buildRespondentGroups(personas, adresses, streetByAddressId),
    [personas, adresses, streetByAddressId]
  );

  // Position ontbreekt op oudere onderzoeken, dus clientside sorteren.
  // AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
  const selectableResearches = useMemo(
    () => [...(allResearches || [])].sort((a, b) => (a.Position || 0) - (b.Position || 0)),
    [allResearches]
  );

  // Handlers
  const researchHandler = (e) => {
    const chosen = selectableResearches.find((r) => r.ID === e.target.value);
    if (!chosen || !chosen.QuestionnaireID) return;
    history.push(buildShareQuestionnairePath({ client, questionnaireID: chosen.QuestionnaireID, researchID: chosen.ID }));
  };

  const measureMomentHandler = (e) => {
    const id = e.target.value;
    setMeasureMomentID(id);
  };

  const personaHandler = (e) => {
    setSelectedPersona(e.target.value);
  };

  /** @param {string} key - Veldnaam van de instelling op het Research-document */
  const linkSettingHandler = (key) => {
    setDisabledSettings((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Vragen kunnen per meetmoment uitstaan; hier — vlak vóór verzenden — moet je
  // zien wat de respondent straks krijgt.
  const selectedMoment = measureMoments.find((mm) => mm.ID === measureMomentID);
  const questionSummary = summarizeMomentQuestions(
    questionnaireFields,
    selectedMoment?.ExcludedFieldIDs
  );

  // Naamstap: ook niet anoniem, maar een medewerker koppelt (geen adres). Een
  // gekozen deelnemer doet dan niets — de server negeert hem — dus de link is
  // altijd de algemene en de keuzelijst staat er niet (testronde 2026-10-13, B5).
  // AI-CONTEXT: See docs/features/deelnemer-koppeling-algemene-link.md
  const researchDoc = Array.isArray(researches) ? researches[0] : null;
  const asksName = !isFlagOn(researchDoc?.AskAddressFirst) && isFlagOn(researchDoc?.AskNameFirst);

  // Normalize persona segment used in links
  const personaOrUndefined =
    asksName || !selectedPersona || selectedPersona === "undefined" ? "undefined" : selectedPersona;

  // Wat op het onderzoek aanstaat, staat standaard aan voor elke link; hier kun
  // je het per link uitzetten. Wat op het onderzoek uitstaat kan een link niet
  // aanzetten, dus dat tonen we niet.
  const linkSettings = LINK_SETTINGS.filter((setting) => isFlagOn(researchDoc?.[setting.key]));
  const asksAddress = isFlagOn(researchDoc?.AskAddressFirst);
  // Alleen wat er nog als schakelaar staat: zet iemand een instelling op het
  // onderzoek uit terwijl deze pagina openstaat, dan hoort hij uit de link.
  const linkSettingsQuery = buildLinkSettingsQuery(
    disabledSettings.filter((key) => linkSettings.some((setting) => setting.key === key))
  );

  // Eén bron voor de invul-URL en de QR-pagina — dezelfde helper die het
  // tabblad Onderzoek bij een huisbezoek gebruikt. De instellingen per link
  // reizen mee als query string; het printvel is papier en heeft ze niet nodig.
  const baseLinks = buildQuestionnaireShareLinks({
    client,
    questionnaireID: questionnaire,
    measureMomentID,
    researchID: research,
    respondentID: personaOrUndefined,
    language: selectedLanguage,
  });
  const shareLinks = baseLinks && {
    ...baseLinks,
    open: `${baseLinks.open}${linkSettingsQuery}`,
    qrcode: `${baseLinks.qrcode}${linkSettingsQuery}`,
  };

  return (
    <div className="main">
      <div className="main-container">
        <div className="page-header">
          <h1>Vragenlijst delen</h1>
        </div>
        <div className="dashboard-container">
          <div className="wizard-section-container">
            <div className="text-section">
              <div className="start-research-step-title-container">
                <h2>Selecteer onderzoek</h2>
                {research ? (
                  researches && researches.map((r) => <p key={r.ID}>{r.Title}</p>)
                ) : !researchesLoaded ? (
                  <p>Onderzoeken worden geladen...</p>
                ) : selectableResearches.length === 0 ? (
                  <p>
                    Er zijn nog geen onderzoeken.{" "}
                    <Link to={`/${client}/research`}>Maak eerst een onderzoek aan</Link>.
                  </p>
                ) : (
                  <select onChange={researchHandler} defaultValue="">
                    <option value="">-- Selecteer onderzoek --</option>
                    {selectableResearches.map((r) => (
                      <option key={r.ID} value={r.ID} disabled={!r.QuestionnaireID}>
                        {r.QuestionnaireID
                          ? r.Title
                          : `${r.Title} (nog geen vragenlijst gekoppeld)`}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="start-research-step-title-container">
                <h2>Selecteer taal</h2>
                <select
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  value={selectedLanguage}
                >
                  <option value="">-- Selecteer taal --</option>
                  {languages.map((language) => (
                    <option key={language.code} value={language.code}>
                      {language.flag} {language.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Respondent selector shown when there are deelnemers or huishoudens */}
              {asksName ? (
                <div className="start-research-step-title-container">
                  <h2>Deelnemer</h2>
                  <p>Dit onderzoek vraagt eerst om de naam. Daarna koppel je de antwoorden zelf aan de juiste deelnemer.</p>
                </div>
              ) : respondentGroups.length > 0 ? (
                <div className="start-research-step-title-container">
                  <h2>Selecteer deelnemer of huishouden</h2>
                  {/* Met de adresstap is de meting niet anoniem: de server koppelt op
                      het ingevulde adres (testronde 2026-10-12, B6). */}
                  {asksAddress && (
                    <p>Dit onderzoek vraagt eerst om het adres. Het ingevulde adres bepaalt bij welk huishouden de antwoorden komen.</p>
                  )}
                  <select onChange={personaHandler} value={selectedPersona}>
                    <option value="undefined">
                      {asksAddress ? "-- Niemand geselecteerd (het adres wordt gevraagd) --" : "-- Niemand geselecteerd (meting anoniem) --"}
                    </option>
                    {respondentGroups.map((group) => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              ) : (
                <div />
              )}

              <div className="start-research-step-title-container">
                <h2>Selecteer meetmoment</h2>
                <select onChange={measureMomentHandler} value={measureMomentID}>
                  <option value="">-- selecteer meetmoment --</option>
                  {measureMoments &&
                    measureMoments.map((mm) => (
                      <option key={mm.ID} value={mm.ID}>
                        {mm.Title}
                      </option>
                    ))}
                </select>

                {selectedMoment && questionSummary.total > 0 && (
                  <p className="share-questionnaire-moment-questions">
                    {questionSummary.active === questionSummary.total
                      ? `Dit meetmoment bevat alle ${questionSummary.total} vragen.`
                      : `Dit meetmoment bevat ${questionSummary.active} van de ${questionSummary.total} vragen.`}
                    {questionSummary.active !== questionSummary.total && (
                      <Link to={`/${client}/researchbuilder/${research}`}>Aanpassen</Link>
                    )}
                  </p>
                )}
              </div>

              <div
                className="share-questionnaire-container"
                style={{ display: measureMomentID === "" ? "none" : "block" }}
              >
                <div className="start-research-step-title-container">
                  <h2>Start onderzoek</h2>
                </div>

                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        {client === "e5a0007e-6f2c-4b57-8258-2376abffacca" && (
                          <>
                            <th>EMAIL</th>
                            <th>WHATSAPP LINK</th>
                            <th>ZUIL LINK (FANCY)</th>
                            <th>ZUIL LINK (SAAI)</th>
                          </>
                        )}
                        <th>LINK</th>
                        <th>QR-CODE</th>
                        <th>PRINTEN</th>
                        {/* <th>AI</th> */}
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        {client === "e5a0007e-6f2c-4b57-8258-2376abffacca" && (
                          <>
                            <td>
                              <Tooltip content="Stuur de vragenlijst per email" top="-60px">
                                <EmailOutlinedIcon
                                  className="icon-margin-left"
                                  onClick={() =>
                                    history.push(
                                      `/${client}/emailquestionnaire/${questionnaire}/${measureMomentID}/${research}/${personaOrUndefined}/open/${selectedLanguage || "nl"}`
                                    )
                                  }
                                />
                              </Tooltip>
                            </td>

                            <td>
                              <WhatsAppLink
                                link={shareLinks?.open}
                              />
                            </td>

                            <td>
                              <KioskLink
                                link={shareLinks?.open}
                              />
                            </td>

                            <td>
                              <KioskLinkBoring
                                link={shareLinks?.open}
                              />
                            </td>
                          </>
                        )}

                        <td>
                          <CopyLink
                            link={shareLinks?.open}
                            showOpen
                          />
                        </td>

                        <td>
                          <Tooltip content="Print de QRcode" top="-40px">
                            <img
                              src={QrcodeIcon}
                              id="share-questionnaire-qrcode-icon"
                              alt=""
                              onClick={() => shareLinks && history.push(shareLinks.qrcode)}
                            />
                          </Tooltip>
                        </td>

                        <td>
                          {/* Een echte knop, geen klikbaar icoon: dit is de enige weg
                              naar het printvel, en een tooltip is op een aanraakscherm
                              geen label.
                              AI-CONTEXT: See docs/pitfalls.md#ui-hover-only-affordance-dies-on-mobile */}
                          <Tooltip content="Print de vragenlijst" top="-40px">
                            <button
                              type="button"
                              className="share-questionnaire-icon-button"
                              aria-label="Print de vragenlijst"
                              onClick={() => shareLinks && history.push(shareLinks.print)}
                            >
                              <LocalPrintshopIcon className="icon-margin-left" />
                            </button>
                          </Tooltip>
                        </td>

                        {/* <td>
                          <Tooltip content="AI interviewer" top="-40px">
                            <SmartToyOutlinedIcon
                              className="icon-margin-left"
                              onClick={() =>
                                history.push(
                                  `/${client}/aiinterviewer/${questionnaire}/${measureMomentID}/${research}/${personaOrUndefined}/open/${selectedLanguage}`
                                )
                              }
                            />
                          </Tooltip>
                        </td> */}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {linkSettings.length > 0 && (
                  <div className="start-research-step-title-container share-questionnaire-link-settings">
                    <h2>Instellingen voor deze link</h2>
                    <div className="research-settings-group">
                    {linkSettings.map((setting) => (
                      <div key={setting.key} className="reasearch-builder-option-container">
                        <div className="research-builder-options-title-container">
                          <p id={`link-setting-${setting.key}`}><b>{setting.label}</b></p>
                        </div>
                        <div className="toggle-container">
                          {/* Bewust geen ToggleButton: die slaat op in het
                              onderzoek en verandert dan de standaard van elke link. */}
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              aria-labelledby={`link-setting-${setting.key}`}
                              checked={!disabledSettings.includes(setting.key)}
                              onChange={() => linkSettingHandler(setting.key)}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareQuestionnaire;
